/**
 * businessHours — utilitário puro para computar o status de abertura do
 * negócio a partir do array `horarios` (formato `Horario` do backend).
 *
 * Horario = { dia: 'segunda'|'terca'|...|'domingo',
 *             abre: 'HH:MM', fecha: 'HH:MM', fechado: boolean }
 *
 * `abre`/`fecha` são horas locais (America/Sao_Paulo). Para consistência
 * cross-timezone, computamos o "agora" também em horário local do fuso
 * do usuário — a maioria acessa dentro do próprio município, portanto
 * essa aproximação é suficiente. Feriados e horários especiais NÃO são
 * considerados (basta o dono manter o `fechado` correto no dia).
 */

const DIA_KEY = ['domingo', 'segunda', 'terca', 'quarta', 'quinta', 'sexta', 'sabado'];

function toMinutes(hhmm) {
  if (!hhmm || typeof hhmm !== 'string') return null;
  const [h, m] = hhmm.split(':');
  const hh = parseInt(h, 10);
  const mm = parseInt(m, 10) || 0;
  if (Number.isNaN(hh)) return null;
  return hh * 60 + mm;
}

/**
 * Retorna true se o negócio está aberto AGORA.
 * Casos que retornam false:
 *   - horarios ausente/vazio
 *   - dia atual marcado `fechado: true`
 *   - horário fora do range [abre, fecha) do dia atual
 *   - `abre` ou `fecha` inválidos
 *
 * Suporta janelas que atravessam a meia-noite (ex.: 22:00 → 03:00).
 */
export function isBusinessOpenNow(horarios, referenceDate) {
  if (!Array.isArray(horarios) || horarios.length === 0) return false;

  const now = referenceDate || new Date();
  const currentDayKey = DIA_KEY[now.getDay()];
  const currentMin = now.getHours() * 60 + now.getMinutes();

  const todayEntry = horarios.find((h) => h && h.dia === currentDayKey);
  if (!todayEntry || todayEntry.fechado) {
    // Verifica janela do dia anterior que atravessa a meia-noite
    const yesterdayKey = DIA_KEY[(now.getDay() + 6) % 7];
    const yEntry = horarios.find((h) => h && h.dia === yesterdayKey);
    if (yEntry && !yEntry.fechado) {
      const yOpen = toMinutes(yEntry.abre);
      const yClose = toMinutes(yEntry.fecha);
      if (yOpen != null && yClose != null && yClose < yOpen) {
        // Janela cruza meia-noite. Hoje bem cedo pode estar aberto.
        if (currentMin < yClose) return true;
      }
    }
    return false;
  }

  const openMin = toMinutes(todayEntry.abre);
  const closeMin = toMinutes(todayEntry.fecha);
  if (openMin == null || closeMin == null) return false;

  // Janela que cruza meia-noite (ex.: 22:00 → 03:00): abre[22*60]=1320 >
  // fecha[3*60]=180. Considera aberto se hora atual está >= abre OU
  // < fecha. Uma janela do dia SEGUINTE cobre esse caso ao cruzar 0h.
  if (closeMin <= openMin) {
    return currentMin >= openMin || currentMin < closeMin;
  }

  return currentMin >= openMin && currentMin < closeMin;
}

/**
 * True quando o negócio tem uma promoção ATIVA cadastrada (não apenas a
 * flag booleana antiga `promocao=true` que indicava "aceita promover").
 *
 * O backend pode enviar:
 *   - `promocao_ativa`: objeto (cupom/campanha ativo) OU null
 *   - `tem_promocao_ativa`: boolean derivado (nem sempre populado)
 *
 * Retorna true APENAS quando um dos dois campos indica promoção ativa
 * concreta. `business.promocao` sozinho (checkbox de cadastro) NÃO
 * conta — evita mostrar "Promoção" em cards que não têm nenhuma.
 */
export function hasActivePromotion(business) {
  if (!business) return false;
  if (business.promocao_ativa && typeof business.promocao_ativa === 'object') return true;
  if (business.tem_promocao_ativa === true) return true;
  return false;
}
